import {Component, DestroyRef, inject, signal, ChangeDetectionStrategy} from "@angular/core"
import type {CmLogger} from "@cm/log"
import {CM_LOGGER_AUTH} from "@common/services/logging"
import {ReactiveFormsModule, UntypedFormControl, UntypedFormGroup, Validators} from "@angular/forms"
import {MatButtonModule} from "@angular/material/button"
import {MatCardModule} from "@angular/material/card"
import {MatInputModule} from "@angular/material/input"
import {MatProgressSpinnerModule} from "@angular/material/progress-spinner"
import {MatSnackBar} from "@angular/material/snack-bar"
import {MatTooltipModule} from "@angular/material/tooltip"
import {ActivatedRoute, Router} from "@angular/router"
import {SiloService} from "@common/services/auth/silo.service"
import {InputContainerComponent} from "@common/components/inputs/input-container/input-container.component"
import {mutateThrowingErrors} from "@common/helpers/api/mutate"
import {getPasswordHash} from "@common/helpers/auth/hash"
import {isSafeReturnUrl} from "@common/helpers/safe-redirect/safe-redirect"
import {AuthService} from "@common/services/auth/auth.service"
import {NotificationsService} from "@common/services/notifications/notifications.service"
import {OrganizationsService} from "@common/services/organizations/organizations.service"
import {LoginResetPasswordGQL, LoginSendPasswordResetEmailGQL} from "@pages/login/login-page.generated"
import {NgOptimizedImage} from "@angular/common"
import {environment} from "@environment"
import {Settings} from "@app/common/models/settings/settings"
import {v4 as uuid4} from "uuid"

@Component({
    changeDetection: ChangeDetectionStrategy.OnPush,
    selector: "cm-login-page",
    templateUrl: "login-page.component.html",
    styleUrls: ["login-page.component.scss"],
    imports: [
        MatCardModule,
        MatInputModule,
        ReactiveFormsModule,
        MatProgressSpinnerModule,
        MatButtonModule,
        InputContainerComponent,
        MatTooltipModule,
        NgOptimizedImage,
    ],
})
export class LoginPageComponent {
    readonly $showProgressCircle = signal(false)
    passwordResetEmail = ""
    readonly $forgotPassword = signal(false)
    readonly $passwordToken = signal<string | undefined>(undefined)
    // "welcome" flow = an invited user completing their registration (as opposed to a plain password reset)
    readonly $isWelcomeFlow = signal(false)
    // Set once the user first presses the submit button; password errors stay hidden until then.
    readonly $passwordSubmitAttempted = signal(false)

    // Must match the backend PASSWORD_MIN_LENGTH. The password is hashed client-side before it is sent,
    // so the backend cannot enforce the real length for this flow — the frontend is the only gate.
    static readonly PASSWORD_MIN_LENGTH = 8

    loginForm = new UntypedFormGroup({
        loginEmailFormControl: new UntypedFormControl("", [Validators.required, Validators.email]),
        loginPasswordFormControl: new UntypedFormControl("", [Validators.required]),
    })

    forgotPasswordForm = new UntypedFormGroup({
        passwordResetEmailFormControl: new UntypedFormControl("", [Validators.required, Validators.email]),
    })

    passwordResetForm = new UntypedFormGroup({
        firstNameFormControl: new UntypedFormControl(""),
        lastNameFormControl: new UntypedFormControl(""),
        passwordVariantOneFormControl: new UntypedFormControl("", [Validators.required, Validators.minLength(LoginPageComponent.PASSWORD_MIN_LENGTH)]),
        passwordVariantTwoFormControl: new UntypedFormControl("", [Validators.required]),
    })

    readonly $cardHeading = signal("Reset Password")

    readonly $googleSsoLink = signal<string | undefined>(undefined)
    readonly $microsoftSsoLink = signal<string | undefined>(undefined)

    readonly destroyRef = inject(DestroyRef)
    readonly organizations = inject(OrganizationsService)
    readonly silo = inject(SiloService)

    readonly notifications = inject(NotificationsService)
    readonly sendPasswordResetEmailGql = inject(LoginSendPasswordResetEmailGQL)
    readonly resetPasswordGql = inject(LoginResetPasswordGQL)
    private readonly logger: CmLogger = inject(CM_LOGGER_AUTH)
    readonly auth = inject(AuthService)
    private readonly snackBar = inject(MatSnackBar)
    private readonly router = inject(Router)
    private readonly route = inject(ActivatedRoute)

    constructor() {
        this.$showProgressCircle.set(false)
        this.route.queryParams.subscribe((params) => {
            if (params.token) {
                this.$passwordToken.set(params.token)
            }
            if (params.flow === "welcome") {
                this.$cardHeading.set("Welcome to colormass")
                this.$isWelcomeFlow.set(true)
                // Invited users complete their profile here, so require their name in this flow only.
                this.passwordResetForm.controls.firstNameFormControl.setValidators([Validators.required])
                this.passwordResetForm.controls.lastNameFormControl.setValidators([Validators.required])
                this.passwordResetForm.controls.firstNameFormControl.updateValueAndValidity()
                this.passwordResetForm.controls.lastNameFormControl.updateValueAndValidity()
            }
        })
        this.$googleSsoLink.set(this.getGoogleSsoLink())
        this.$microsoftSsoLink.set(this.getMicrosoftSsoLink())
    }

    async login(email?: string, password?: string): Promise<void> {
        this.$showProgressCircle.set(true)
        try {
            const credentials = {
                email: email ?? this.loginForm.controls.loginEmailFormControl.value,
                password: password ?? this.loginForm.controls.loginPasswordFormControl.value,
            }
            const success = await this.auth.logIn(credentials)
            if (success) {
                const returnUrl = this.route.snapshot.queryParams.returnUrl
                if (isSafeReturnUrl(returnUrl)) {
                    void this.router.navigateByUrl(returnUrl)
                } else {
                    await this.auth.navigateToDefaultView()
                }
            }
        } catch (error) {
            this.logger.error("Login failed", error)
            // TODO: deal with other errors (e.g. server unavailable) ?
            this.snackBar.open("Login failed. Wrong email or password.", "", {duration: 3000})
        } finally {
            this.$showProgressCircle.set(false)
        }
    }

    async sendForgotPasswordEmail(email: string) {
        try {
            this.$showProgressCircle.set(true)
            await mutateThrowingErrors(this.sendPasswordResetEmailGql)({input: {email}})
            this.notifications.showInfo(
                "If this account exists, you will receive an email to help you reset your password, please check your inbox in a bit.",
                10000,
            )
        } catch (error) {
            this.notifications.showError(error)
        } finally {
            this.$showProgressCircle.set(false)
        }
    }

    onSubmitPassword() {
        this.$passwordSubmitAttempted.set(true)
        const password = this.passwordResetForm.controls.passwordVariantOneFormControl.value
        const passwordsMatch = password === this.passwordResetForm.controls.passwordVariantTwoFormControl.value
        // Button stays enabled; the errors surfaced here (too-short password, mismatch, missing name) are the gate.
        if (this.passwordResetForm.invalid || !passwordsMatch) {
            return
        }
        void this.setPassword(password)
    }

    async setPassword(newPassword: string) {
        this.$showProgressCircle.set(true)
        // Make sure to clear existing tokens.
        this.auth.logOut()
        const newPasswordHash: string = getPasswordHash(newPassword)
        const token = this.$passwordToken()
        // Only sent when an invited user completes registration; a plain password reset leaves the name untouched.
        const firstName = this.$isWelcomeFlow() ? this.passwordResetForm.controls.firstNameFormControl.value?.trim() || undefined : undefined
        const lastName = this.$isWelcomeFlow() ? this.passwordResetForm.controls.lastNameFormControl.value?.trim() || undefined : undefined
        if (token) {
            await this.notifications.withUserFeedback(
                async () => {
                    const email = (
                        await mutateThrowingErrors(this.resetPasswordGql)({
                            input: {
                                token,
                                password: newPasswordHash,
                                firstName,
                                lastName,
                            },
                        })
                    ).resetPassword
                    void this.login(email, newPassword)
                    this.$showProgressCircle.set(false)
                },
                {
                    success: "Your password has been changed successfully",
                    error: "Cannot change password",
                },
            )
        }
    }

    getGoogleSsoLink() {
        const clientId = environment.auth.google.clientId
        if (clientId) {
            const queryParams = new URLSearchParams({
                client_id: clientId,
                // redirect to the SSO callback handler page after the login with the identity provider
                redirect_uri: `${environment.platformDeployUrl}/sso/google`,
                response_type: "code",
                scope: "openid email",
                // this redirectUri comes from the query params and
                // refers to a page on the platform the user should be taken to after completion of all login steps
                state: btoa(JSON.stringify({csrfToken: this.auth.csrfToken, redirectUri: this.route.snapshot.queryParams.returnUrl})),
            })
            return `${Settings.GOOGLE_OAUTH_ENDPOINT}?${queryParams}`
        }
        return undefined
    }

    getMicrosoftSsoLink() {
        const clientId = environment.auth.microsoft.clientId
        if (clientId) {
            const queryParams = new URLSearchParams({
                client_id: clientId,
                redirect_uri: `${environment.platformDeployUrl}/sso/microsoft`,
                response_type: "code",
                scope: "openid profile email offline_access",
                state: btoa(JSON.stringify({csrfToken: this.auth.csrfToken, redirectUri: this.route.snapshot.queryParams.returnUrl})),
                response_mode: "fragment",
                nonce: uuid4(),
            })
            return `${Settings.MICROSOFT_OAUTH_ENDPOINT}?${queryParams}`
        }
        return undefined
    }
}
